import { useEffect, useLayoutEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";

import { useResourceContext } from "../features/context/ResourceContext";
import { resourceState } from "../features/recoil";
import { COMN_CODE } from "../constants";
import { useTheme } from "../hooks";
import { utils } from "../utils";

type TResource = {
    area: string;
    comnCd?: string;
};
type UseResourceProps = {
    defaultSchema: TResource[];
};

type UseResourceReturn = {
    getResource: (area: string, comnCd?: string) => any;
    updateResource: (area: string, comnCd?: string) => any;
};

// const RENEWAL_CYCLE = 10000;
const RENEWAL_CYCLE = 86400000;

export const getResourceKey = (area: string, comnCd?: string, lang?: string) => {
    return area + (comnCd ? `:${comnCd}` : "") + (lang ? `;${lang}` : "");
};

const getResourceFromIDB = (key: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("TANCIS");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            const db = request.result;
            const ts = db.transaction("RESOURCE", "readonly");
            const os = ts.objectStore("RESOURCE");
            const get = os.get(key);
            get.onsuccess = () => {
                resolve(get.result);
            };
        };
    });
};

const addResourceToIDB = (record: any) => {
    record.count = record.value.length;
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("TANCIS");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const add = os.add(record);
                add.onsuccess = () => {
                    const { value, ...rest } = record;
                    const os_ref = ts.objectStore("RESOURCE_REF");
                    const add_ref = os_ref.add(rest);
                    // @ts-ignore
                    resolve();
                };
            } catch (error) {
                indexedDB.deleteDatabase("TANCIS");
                reject();
            }
        };
    });
};

const putResourceToIDB = (record: any) => {
    record.count = record.value.length;
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("TANCIS");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const put = os.put(record);
                put.onsuccess = () => {
                    const { value, ...rest } = record;
                    const os_ref = ts.objectStore("RESOURCE_REF");
                    const put_ref = os_ref.put(rest);
                    // @ts-ignore
                    resolve();
                };
            } catch (error) {
                indexedDB.deleteDatabase("TANCIS");
                reject();
            }
        };
    });
};

const ____ = (key: any, value: any) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open("TANCIS");
        request.onupgradeneeded = () => {
            const db = request.result;
            db.createObjectStore("RESOURCE", { keyPath: "key" });
            db.createObjectStore("RESOURCE_REF", { keyPath: "key" });
        };
        request.onsuccess = async () => {
            try {
                const db = request.result;
                const ts = db.transaction(["RESOURCE", "RESOURCE_REF"], "readwrite");
                const os = ts.objectStore("RESOURCE");
                const get = os.get(key);
                get.onsuccess = () => {
                    if (get.result) {
                        const record = { ...get.result, value, updated: new Date() };
                        const put = os.put(record);
                        put.onsuccess = () => {
                            const { value, ...rest } = record;
                            const os_ref = ts.objectStore("RESOURCE_REF");
                            os_ref.put(rest);
                            resolve(record);
                        };
                    } else {
                        const now = new Date();
                        const record = { key, value, count: value.length, created: now, updated: now };
                        const add = os.add(record);
                        add.onsuccess = () => {
                            const { value, ...rest } = record;
                            const os_ref = ts.objectStore("RESOURCE_REF");
                            os_ref.add(rest);
                            resolve(record);
                        };
                    }
                };
            } catch (error) {
                indexedDB.deleteDatabase("TANCIS");
                reject();
            }
        };
    });
};

export const useResource = (props: UseResourceProps): UseResourceReturn => {
    const { theme } = useTheme();
    const setResource = useSetRecoilState(resourceState);

    const { defaultSchema } = props;

    const { asyncNormal, getResource, updateResource } = useResourceContext();

    // const updateResource = async (area: string, comnCd?: string) => {
    //     const key = getResourceKey(area, comnCd, theme.lang);
    //     try {
    //         const code = await utils.getCode({ area, comnCd });
    //         const value = Object.values<any>(code.data)?.[0]?.content || [];
    //         const record = await ____(key, value);
    //         setResource((prev: any) => ({ ...prev, [key]: record }));
    //     } catch (error) {
    //         console.log(error);
    //     }
    // };

    useLayoutEffect(() => {
        defaultSchema.forEach(({ area, comnCd }) => {
            const code = COMN_CODE[area];
            if (!code || code.async) return;
            try {
                asyncNormal(area, comnCd);
            } catch (error) {}
        });
    }, [theme.lang]);

    return {
        getResource: (...params) => {
            return getResource(...params)?.data;
        },
        updateResource: async (...params) => {
            const { key, updated } = await updateResource(...params);
            setResource((prev: any) => ({ ...prev, [key]: updated }));
        },
    };
};
